import { useQuery } from '@tanstack/react-query'
import { useStoProject } from './useStoProject'
import { stoQueries } from './queries/stoQueries'
import { pickContent, StoContentTypes, withAcceptedCurrencies } from 'models/projectContent'
import { resolveSectionState, type SectionState } from 'models/stoSection'
import { metricAnchor } from 'constants/stoAnchors'
import { getMetricHref, resolveMetricValue, type Metric } from 'models/metric'
import type { MoneyDisplay } from 'shared/domain/projectTemplate'
import type { ComputedFields } from 'models/productChart'
import { useMoneyDisplay } from './useMoneyDisplay'
import { useComputedFields } from './useProductChart'

export interface HeroMetricEntry {
  title: string
  text: string
  href?: string
  tooltip?: string
  tooltipDisclaimer: boolean
  tooltipPastPerformanceDisclaimer: boolean
}

export interface HeroMetric {
  /** Stable per render — the key when authored, otherwise the position. */
  id: string
  /** Only when the author set a key: an anchor is a public link, never generated. */
  anchor?: string
  label: string
  value: string
  href?: string
  tooltip?: string
  tooltipDisclaimer: boolean
  tooltipPastPerformanceDisclaimer: boolean
  entries: HeroMetricEntry[]
  note?: string
}

const clean = (text: string | undefined): string => text?.trim() ?? ''

/** Back-office `HERO_METRICS_COUNT` / `KEY_TERMS_COUNT`. */
const MAX_HERO_METRICS = 4
const MAX_KEY_TERMS = 3

/**
 * Same rules as the back-office preview: a metric that resolves to nothing is left
 * out, array order is display order, and at most `max` are shown.
 */
export const toHeroMetrics = (
  metrics: Metric[] | undefined,
  project: unknown,
  max = MAX_HERO_METRICS,
  display?: MoneyDisplay,
  computed?: ComputedFields,
): HeroMetric[] =>
  (metrics ?? [])
    .map((metric, index) => ({ metric, index, value: resolveMetricValue(metric.value, project, display, computed) }))
    .filter((item): item is typeof item & { value: string } => item.value !== undefined)
    .slice(0, max)
    .map(({ metric, index, value }) => {
      const key = clean(metric.key)
      return {
        id: key || String(index),
        anchor: key ? metricAnchor(key) : undefined,
        label: clean(metric.label),
        value,
        href: getMetricHref(value, metric.url),
        tooltip: clean(metric.tooltip) || undefined,
        tooltipDisclaimer: Boolean(metric.tooltipDisclaimer && clean(metric.tooltip)),
        tooltipPastPerformanceDisclaimer: Boolean(metric.tooltipPastPerformanceDisclaimer && clean(metric.tooltip)),
        entries: (metric.entries ?? [])
          .map((entry) => ({
            title: clean(entry.title),
            text: clean(entry.text),
            href: getMetricHref(clean(entry.text), entry.url),
            tooltip: clean(entry.tooltip) || undefined,
            tooltipDisclaimer: Boolean(entry.tooltipDisclaimer && clean(entry.tooltip)),
            tooltipPastPerformanceDisclaimer: Boolean(entry.tooltipPastPerformanceDisclaimer && clean(entry.tooltip)),
          }))
          .filter((entry) => entry.title || entry.text),
        note: clean(metric.note) || undefined,
      }
    })

function useMetricsContent(
  type: typeof StoContentTypes.HERO_METRICS | typeof StoContentTypes.KEY_TERMS,
  max: number,
): SectionState<HeroMetric[]> {
  const { project, projectId } = useStoProject()
  const query = useQuery(stoQueries.content(projectId))
  const display = useMoneyDisplay()
  const computed = useComputedFields()

  return resolveSectionState({
    isPending: query.isPending,
    isError: query.isError,
    data: toHeroMetrics(
      pickContent<Metric[]>(query.data, type),
      withAcceptedCurrencies(project, query.data),
      max,
      display,
      computed,
    ),
    isEmpty: (metrics) => metrics.length === 0,
  })
}

export const useHeroMetrics = (): SectionState<HeroMetric[]> =>
  useMetricsContent(StoContentTypes.HERO_METRICS, MAX_HERO_METRICS)

/** Key terms (Figma 3329:68758): the same metric model and row, three of them. */
export const useKeyTerms = (): SectionState<HeroMetric[]> => useMetricsContent(StoContentTypes.KEY_TERMS, MAX_KEY_TERMS)
